// - [ ] Adjust player to be large with aspect ratio so overlay isn't on black for position consistency.
// - [ ] One overlay-component? Or one overlay component per video instance?
<template>
  <div
    id="overlay-container"
    class="transparent"
    :style="pointerEvents">
    <a
      v-if="exposeYoutubeLink"
      target="_blank"
      id="youtube-redirect"
      class="transparent"
      :href="formattedYoutubeURL">
    </a>
    <div
      v-show='(
        ytInitializing
        || vimeoInitializing
        || panoptoInitializing
      )'
      class='yt-trigger-play'>
      <v-icon :size="sizePlayIcon" color="white">play_arrow</v-icon>
    </div>
    <!-- [ ] refactor how we handle displaying alerts that are overlaid on the video instance -->
    <div v-show="showAlert" id='player-state-change-alert'>
      <v-icon color="white">{{alertIcon}}</v-icon>
    </div>
    <div v-show="showVolumeStatusAlert" id="overlay-container-bezel-text-wrapper">
      <div id="overlay-container-bezel-text">{{volumeLevel}}%</div>
    </div>
    <div v-show="showSeekNavigationStatusAlert" :class="[`overlay-bezel-seek-text-container-${alertIcon}`]">
      <div class="overlay-bezel-seek-text-wrapper">
        <v-icon color="white">{{alertIcon}}</v-icon>
        <div class="overlay-container-bezel-seek-text">{{seekStep}}</div>
      </div>
    </div>
    <div v-if="showReplayAlert" id="player-replay-alert">
      <v-icon color="white">replay</v-icon>
    </div>
    <assistive-status id="overlay-assist" v-if="hasActiveOverlay" label="New hotspot interaction available."></assistive-status>
    <transition-group name="fade">
      <hotspot tabindex="0" :aria-live="isLiveSession ? '' : 'polite'" v-for='(interaction, index) in overlayInteractions' :interaction="interaction" :key="'hs_' + index" :stack-index="index" role="region" aria-label="interaction hotspot"></hotspot>
    </transition-group>
    <div
      v-if="!bulbFinished"
      @click="handleOverlayPlay"
      @keyup.enter="handleOverlayPlay"
      @keyup.space="handleOverlayPlay"
      class='overlay-play-toggle'
      tabindex="0"
      role="button"
      aria-label="toggle video play"
      :class="{ 'liveSession': isLiveSession }">
    </div>
    <div 
      v-if="showReplayAlert"
      @click="handleOverlayReplay"
      @keyup.enter="handleOverlayReplay"
      @keyup.space="handleOverlayReplay"
      class='overlay-replay-toggle'
      ref="playerOverlayReplay"
      tabindex="0"
      role="button"
      aria-label="replay video">
    </div>
  </div>
</template>

<script>

// Custom Vue components: 
import Hotspot from './Hotspot';
import EventBus from '@/buses/EventBus';
import AssistiveStatus from './AssistiveStatus';

// Constants:
import {
  PLAYER_FADE_IN_TRANSITION_TIMES,
} from '@Constants';

import { waitForElementToDisplay } from '@utilities';

// Plugins / Libraries
import _ from 'lodash';
import {
  mapState,
  mapActions,
  mapGetters,
  mapMutations
} from 'vuex';

export default {
  name: 'overlay-container',
  data() {
    return {
      hasActiveOverlay: false,
      showAlert: false,
      showVolumeStatusAlert: false,
      showSeekNavigationStatusAlert: false,
      alertIcon: 'play_arrow',
      showTooltip: false,
      activePanoptoEmbedStreamOniOS: false,
      volumeLevel: 100,
      seekStep: '1 second',
      hasVimeoPlayerFadeTransitionElement: false,
    };
  },
  components: {
    AssistiveStatus,
    Hotspot,
  },
  computed: {
    ...mapState('player', [
      'isLiveSession',
      'isPanoptoEmbeddedIframe',
      'playButtonClicked',
    ]),
    ...mapGetters('interactions', [
      'interactions',
      'overlayInteractions',
      'activeOverlayInteractions'
    ]),
    ...mapGetters('player', [
      'bulb',
      'bulbInitialized',
      'bulbFinished',
      'displayDuration',
      'displayTime',
      'isBroadcast',
      'isBroadcastTeacher',
			'isBroadcastStudent',
      'manualPause',
      'manualPlay',
      'playerState',
      'schoolId',
      'user',
      'youtubeActiveStream',
      'ytInitializing', // Duct tape for youtube in iframes not responding to user clicks. Iframe owner needs to add allow="autoplay *;"
      'ytVr', // duct tape for disabling interaction overlay
      'vimeoInitializing', // Duct tape for Vimeo videos in iframes not responding to user clicks. Iframe owner needs to add allow="autoplay *;"
      'panoptoInitializing', // Duct tape for Panoptos' videos in iframes not responding to user clicks. Iframe owner needs to add allow="autoplay *;"
    ]),
    exposeYoutubeLink() {
      // Expose the YouTube redirect link if the current segment/active stream is YouTube for non-school users
      return this.youtubeActiveStream && !this.schoolId;
    },
    formattedYoutubeURL() {
      if (this.youtubeActiveStream) {
        // Grabs the YT video ID - URL can be doubled
        return `https://youtube.com/watch?v=${this.$getYoutubeVideoId(this.youtubeActiveStream)}`;
      }
      return 'https://www.youtube.com';
    },
    pointerEvents() {
      if (
        (
          this.ytInitializing
          || this.ytVr
        )
        || (
          this.vimeoInitializing
          || this.hasVimeoPlayerFadeTransitionElement
        )
        || this.panoptoInitializing
      ) {
        return {
          pointerEvents: 'none',
        }
      }
    },
    showReplayAlert() {
      if (this.isLiveSession) return false;
			else if (this.bulbFinished && this.isBroadcastTeacher) return true;
			else if (this.bulbFinished && this.isBroadcastStudent) return false;
			return this.bulbFinished;
    },
    sizePlayIcon() {
      if (this.isPanoptoEmbeddedIframe) return 100;
      if (this.activePanoptoEmbedStreamOniOS) return 120;
      return 65
    },
    async hasVimeoPlayerZerothInteractionFadeClass() {
      try {
        const vimeoPlayerTransitionElement = await waitForElementToDisplay('.vimeo-zeroth-interaction-fade-enter-active');
        if (vimeoPlayerTransitionElement) {
          this.hasVimeoPlayerFadeTransitionElement = true;
          setTimeout(() => {
            this.hasVimeoPlayerFadeTransitionElement = false;
          }, PLAYER_FADE_IN_TRANSITION_TIMES.Vimeo.zerothInteraction);
        } 
      } catch (error) {
        // don't need to log out error here, unnecessary and superfluous -- just like pineapple on pizza.
      }
    },
  },
  methods: {
    ...mapActions('player', [
      'replayVideo',
      'togglePlay',
    ]),
    ...mapMutations('player', [
      'setDesiredState',
      'setManualPause',
      'setManualPlay',
      'setPanoptoInitializing',
    ]),
    handleOverlayPlay() {
      if (this.isLiveSession) return;
      this.togglePlay();
    },
    handleOverlayReplay() {
      this.replayVideo();
    },
    handleMuteAlertIcon(muted) {
      this.showVolumeStatusAlert = true;
      if (muted) {
        this.showAlert = true;
        this.volumeLevel = 0;
        this.alertIcon = 'volume_mute';
        setTimeout(() => { this.showAlert = this.showVolumeStatusAlert = false }, 1000);
      }
      if (!muted) {
        this.showAlert = true;
        this.alertIcon = 'volume_up';
        this.volumeLevel = this.bulb.config.volume;
        setTimeout(() => { this.showAlert = this.showVolumeStatusAlert = false }, 1000);
      }
    },
    handleVolumeDownAlertIcon(volume) {
      this.showAlert = true;
      this.showVolumeStatusAlert = true;
      this.volumeLevel = volume.toFixed();
      this.alertIcon = (volume <= 0) ? 'volume_mute' : 'volume_down';
      setTimeout(() => { this.showAlert = this.showVolumeStatusAlert = false }, 550);
    },
    handleVolumeUpAlertIcon(volume) {
      this.showAlert = true;
      this.showVolumeStatusAlert = true;
      this.volumeLevel = volume.toFixed();
      this.alertIcon = 'volume_up';
      setTimeout(() => { this.showAlert = this.showVolumeStatusAlert = false }, 550);
    },
    handleSeekNavigationAlertIcon({
      arrowType,
      seekStep,
      } = {}) {
      this.showSeekNavigationStatusAlert = true;
      this.seekStep = `${seekStep} second${seekStep !== 1 ? 's' : ''}`
      this.alertIcon =  arrowType === 'ArrowRight' ? 'fast_forward' : 'fast_rewind';
      setTimeout(() => { this.showSeekNavigationStatusAlert = false }, 550);
    },
  },
  mounted() {
    /**
     * Is only event invoked IFF the user is using iOS and the current stream type is a Panopto video using
     * the Panopto embed API.
     */
    EventBus.$on('disablePointerEventsForPanoptoOverlay', () => {
      this.setPanoptoInitializing(true);
      this.activePanoptoEmbedStreamOniOS = true;
    });
    EventBus.$on('handleMuteAlertIcon', data => {
      this.handleMuteAlertIcon(data.muted);
    });
    EventBus.$on('handleVolumeDownIcon', data => {
      this.handleVolumeDownAlertIcon(data.volume);
    });
    EventBus.$on('handleVolumeUpIcon', data => {
      this.handleVolumeUpAlertIcon(data.volume);
    });
    EventBus.$on('handlePlayerOverlaySeekStep', data => {
      const { arrowType, seekStep } = data;
      this.handleSeekNavigationAlertIcon({
        arrowType,
        seekStep,
      });
    });
  },
  watch: {
    'activeOverlayInteractions'(n, o) {
      if (n.length > 0 !== this.hasActiveOverlay) {
        this.hasActiveOverlay = n.length > 0;
      }
    },
    'playerState'(n, o) {
      // when player state changes, toggle the value of the icon to retrigger the animation
      this.showAlert = false;
      if (
        this.playerState === 'play' && !this.manualPlay ||
        this.playerState === 'paused' && !this.manualPause
      ) {
        return;
      }
      this.$nextTick(() => {
        this.alertIcon = this.playerState === 'play' ? 'play_arrow' : 'pause';
        this.showAlert = true;
        setTimeout(() => { this.showAlert = false }, 550);
      });
    },
    bulbFinished(n, o) {
      if (n) {
        this.$nextTick( () => {
          this.$refs.playerOverlayReplay.focus();
        });
      }
    },
    playButtonClicked(newValue, oldValue) {
      if (
        newValue
        && this.$parent?.$refs?.videoInstance[0]?.activeStream?.type === 'video/vimeo'
      ) {
        this.hasVimeoPlayerZerothInteractionFadeClass;
      }
    },
  },
};
</script>

<style lang="scss">
  #overlay-container{
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1;
    display: flex;
    align-items: center;
  }

  .card {
    overflow: auto;
    height: 100%;
    margin: 0 !important;
  }

  #player-state-change-alert, .yt-trigger-play {
    position: absolute;
    width: 100%;
		text-shadow: 0 0 3px black;
		text-align: center;
		font-size: 5em;
  	-webkit-animation: fade-test 1s; /* Safari 4+ */
  	-moz-animation:    fade-test 1s; /* Fx 5+ */
  	-o-animation:      fade-test 1s; /* Opera 12+ */
  	animation:         fade-test 1s; /* IE 10+, Fx 29+ */
  }

  #player-state-change-alert {
    opacity: 0;
  }

	#player-state-change-alert i, .yt-trigger-play i {
	  font-size: 10vmin;
	  vertical-align: middle;
	}

  .general-overlay-bezel-seek-container {
    position: absolute;
    z-index: 19;
    opacity: 0;
    background-color: rgba(0,0,0,.6);
    -webkit-animation: fade-test 1.5s; /* Safari 4+ */
  	-moz-animation:    fade-test 1.5s; /* Fx 5+ */
  	-o-animation:      fade-test 1.5s; /* Opera 12+ */
  	animation:         fade-test 1.5s; /* IE 10+, Fx 29+ */
    border-radius: 7px;

    .overlay-bezel-seek-text-wrapper {
      display: flex;
      align-items: center;

      > i {
        margin-left: 5px;
      }
    }

    .overlay-container-bezel-seek-text {
      padding: 10px 20px;
      pointer-events: none;
      color: #FFFFFF;
      text-align: left;
      font-size: 12px;
      font-weight: 500;
      margin: 0px -9px;
    }
  }

  .overlay-bezel-seek-text-container-fast_forward {
    @extend .general-overlay-bezel-seek-container;
    right: 0;
  }

  .overlay-bezel-seek-text-container-fast_rewind {
    @extend .general-overlay-bezel-seek-container;
    left: 0;
  }

  #overlay-container-bezel-text-wrapper {
    text-align: center;
    position: absolute;
    left: 0;
    right: 0;
    top: 3%;
    z-index: 19;
    opacity: 0;
    -webkit-animation: fade-test 1s; /* Safari 4+ */
  	-moz-animation:    fade-test 1s; /* Fx 5+ */
  	-o-animation:      fade-test 1s; /* Opera 12+ */
  	animation:         fade-test 1s; /* IE 10+, Fx 29+ */
  }

  #overlay-container-bezel-text {
    display: inline-block;
    padding: 10px 20px;
    font-size: 2em;
    background: rgba(0,0,0,0.5);
    pointer-events: none;
    border-radius: 3px;
    color: #FFFFFF;
  }

  #player-replay-alert {
    position: absolute;
    width: 100%;
		text-shadow: 0 0 3px black;
		text-align: center;
		font-size: 5em;
    -webkit-animation: fadeInUp 1s; /* Safari 4+ */
  	-moz-animation:    fadeInUp 1s; /* Fx 5+ */
  	-o-animation:      fadeInUp 1s; /* Opera 12+ */
  	animation:         fadeInUp 1s; /* IE 10+, Fx 29+ */
  }

  #player-replay-alert i {
    font-size: 10vmin;
	  vertical-align: middle;
  }

	/*@-webkit-keyframes fade-test {*/
	/*  0%   { opacity: 1; }*/
	/*  40%  { opacity: 1; }*/
	/*  100% { opacity: 0; transform: scale(2);}*/
	/*}*/
	/*@-moz-keyframes fade-test {*/
	/*  0%   { opacity: 1; }*/
	/*  40%  { opacity: 1; }*/
	/*  100% { opacity: 0; transform: scale(2);}*/
	/*}*/
	/*@-o-keyframes fade-test {*/
	/*  0%   { opacity: 1; }*/
	/*  40%  { opacity: 1; }*/
	/*  100% { opacity: 0; transform: scale(2);}*/
	/*}*/
	@keyframes fade-test {
	  0%   { opacity: 1; }
	  40%  { opacity: 1; }
	  100% { opacity: 0; transform: scale(1.2);}
	}

  @keyframes fadeInUp {
    0% {
        opacity: 0;
        transform: translateY(40px);
    }
    100% {
        opacity: 1;
        transform: translateY(0);
    }
}
  
	.hover:hover{
	  cursor: pointer;
	}

	.overlay-play-toggle, .overlay-replay-toggle {
	  position: relative;
	  width: 100%;
	  height: 100%;
	}
	#youtube-redirect {
	  bottom: 0;
	  right: 0;
	  width: 125px;
	  height: 40px;
	  position: absolute;
	  z-index: 2;
	}
	.fade-leave-active {
  transition: opacity .5s;
  }
  /* Disable click events on the overlay if it is a liveSession */
  .liveSession {
    pointer-events: none;
  }
</style>
